import type { ParsedStreamData } from '@sweetistics/contracts/chat-stream';
import { pickBy } from 'es-toolkit';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useRef } from 'react';
import type { StreamingLogger } from '@/components/chat/chat-helpers';
import type { Message, ReasoningTraceEntry, ToolInvocation } from '@/components/chat/chat-types';
import { formatDisplayValue, formatLabel, pluralize } from '@/components/chat/tool-execution/utils';
import { isPlainObject } from '@/lib/utils/type-guards';
import { normalizeToolArgs, normalizeToolResult } from '@/lib/validation/api/chat-stream-helpers';
import { appendReasoningTraceEntry, applyToolResult, updateReasoningTraceEntry, upsertToolInvocation } from './helpers';
import type { StreamingUIEvent, ToolEvent } from './types';

type UseStreamingToolsOptions = {
  log: StreamingLogger;
  setMessages: Dispatch<SetStateAction<Message[]>>;
  onUiEvent?: (event: StreamingUIEvent) => void;
};

type ToolEventHandler = (assistantId: string, event: ParsedStreamData) => boolean;

const isToolEvent = (event: ParsedStreamData): event is ToolEvent => event.type.startsWith('tool-');

const getPlainRecord = (value: unknown): Record<string, unknown> | null => (isPlainObject(value) ? value : null);

export const useStreamingTools = ({ log, setMessages, onUiEvent }: UseStreamingToolsOptions) => {
  const toolNameCache = useRef(new Map<string, string>());
  const appendToolCall = useCallback(
    (
      assistantId: string,
      invocation: {
        toolCallId: string;
        toolName: string;
        args?: Record<string, unknown> | undefined;
        state?: ToolInvocation['state'];
      }
    ) => {
      setMessages((previous) => upsertToolInvocation(previous, assistantId, invocation));
    },
    [setMessages]
  );

  const applyResult = useCallback(
    (
      assistantId: string,
      resultUpdate: {
        toolCallId: string;
        result?: Record<string, unknown> | string | number | null | unknown[] | undefined;
        state?: ToolInvocation['state'];
      }
    ) => {
      setMessages((previous) => applyToolResult(previous, assistantId, resultUpdate));
    },
    [setMessages]
  );

  const appendReasoning = useCallback(
    (assistantId: string, entry: ReasoningTraceEntry) => {
      setMessages((previous) => appendReasoningTraceEntry(previous, assistantId, entry));
    },
    [setMessages]
  );

  const updateReasoning = useCallback(
    (assistantId: string, entryId: string, updater: (entry: ReasoningTraceEntry) => ReasoningTraceEntry) => {
      setMessages((previous) => updateReasoningTraceEntry(previous, assistantId, entryId, updater));
    },
    [setMessages]
  );

  const buildArgumentsSummary = useCallback((arguments_: unknown): string | undefined => {
    const record = getPlainRecord(arguments_);
    if (!record) {
      return;
    }
    const filtered = pickBy(record, (value) => value !== undefined && value !== null);
    const entries = Object.entries(filtered);
    if (entries.length === 0) {
      return;
    }
    const parts = entries.slice(0, 3).map(([key, value]) => `${formatLabel(key)}: ${formatDisplayValue(value)}`);
    if (entries.length > parts.length) {
      parts.push('…');
    }
    return parts.join(' · ');
  }, []);

  const buildResultSummary = useCallback((result: unknown): string => {
    if (result === null || result === undefined) {
      return 'No data returned';
    }
    if (Array.isArray(result)) {
      return result.length === 0 ? 'Empty result set' : pluralize(result.length, 'item');
    }
    const record = getPlainRecord(result);
    if (record) {
      const filtered = pickBy(record, (value) => value !== undefined && value !== null);
      const keys = Object.keys(filtered);
      if (keys.length === 0) {
        return 'Result object is empty';
      }
      const preview = keys
        .slice(0, 3)
        .map((key) => `${formatLabel(key)}: ${formatDisplayValue(filtered[key])}`)
        .join(' · ');
      return keys.length > 3 ? `${preview} · …` : preview;
    }
    return formatDisplayValue(result);
  }, []);

  const createReasoningEntry = useCallback(
    (toolCallId: string, toolName: string, detail?: string): ReasoningTraceEntry => {
      const entry: ReasoningTraceEntry = {
        id: toolCallId,
        type: 'tool-call',
        title: formatLabel(toolName),
        timestamp: Date.now(),
      };
      if (typeof detail === 'string' && detail.length > 0) {
        entry.detail = detail;
      }
      return entry;
    },
    []
  );

  const emitToolResult = useCallback(
    (
      assistantId: string,
      toolCallId: string,
      { outcome, summary, defaultName }: { outcome: 'success' | 'error'; summary: string; defaultName?: string }
    ) => {
      const toolName = toolNameCache.current.get(toolCallId) ?? defaultName ?? toolCallId;
      updateReasoning(assistantId, toolCallId, (entry) => ({
        ...entry,
        type: 'tool-result',
        detail: summary,
        timestamp: Date.now(),
      }));
      onUiEvent?.({
        type: 'tool-result',
        assistantId,
        toolCallId,
        toolName,
        outcome,
        summary,
      });
      toolNameCache.current.delete(toolCallId);
    },
    [onUiEvent, updateReasoning]
  );

  const handleToolEvent = useCallback<ToolEventHandler>(
    (assistantId, event) => {
      if (!isToolEvent(event)) {
        return false;
      }

      switch (event.type) {
        case 'tool-call': {
          log.info('Parsed event: tool-call', { tool: event.toolName, toolCallId: event.toolCallId });
          const argsRecord = getPlainRecord(event.args);
          appendToolCall(assistantId, {
            toolCallId: event.toolCallId,
            toolName: event.toolName,
            args: argsRecord ?? undefined,
            state: 'call',
          });
          toolNameCache.current.set(event.toolCallId, event.toolName);
          const summary = buildArgumentsSummary(argsRecord ?? undefined);
          appendReasoning(assistantId, createReasoningEntry(event.toolCallId, event.toolName, summary));
          onUiEvent?.({
            type: 'tool-call',
            assistantId,
            toolCallId: event.toolCallId,
            toolName: event.toolName,
            summary,
          });
          return true;
        }
        case 'tool-input-start': {
          log.info('Parsed event: tool-input-start', { tool: event.toolName, toolCallId: event.toolCallId });
          appendToolCall(assistantId, {
            toolCallId: event.toolCallId,
            toolName: event.toolName,
            args: {},
            state: 'partial-call',
          });
          toolNameCache.current.set(event.toolCallId, event.toolName);
          updateReasoning(assistantId, event.toolCallId, (entry) => ({
            ...entry,
            detail: entry.detail ?? 'Preparing tool input',
            timestamp: Date.now(),
          }));
          return true;
        }
        case 'tool-input-available': {
          const arguments_ = normalizeToolArgs(event.input);
          log.info('Parsed event: tool-input-available', {
            tool: event.toolName,
            toolCallId: event.toolCallId,
            argKeys: Object.keys(arguments_),
          });
          appendToolCall(assistantId, {
            toolCallId: event.toolCallId,
            toolName: event.toolName,
            args: arguments_,
            state: 'call',
          });
          const summary = buildArgumentsSummary(arguments_);
          if (summary) {
            updateReasoning(assistantId, event.toolCallId, (entry) => ({
              ...entry,
              detail: summary,
              timestamp: Date.now(),
            }));
            onUiEvent?.({
              type: 'tool-call',
              assistantId,
              toolCallId: event.toolCallId,
              toolName: event.toolName,
              summary,
            });
          }
          return true;
        }
        case 'tool-input-error': {
          const arguments_ = normalizeToolArgs(event.input);
          log.error('Parsed event: tool-input-error', {
            tool: event.toolName,
            toolCallId: event.toolCallId,
            error: event.errorText,
          });
          appendToolCall(assistantId, {
            toolCallId: event.toolCallId,
            toolName: event.toolName,
            args: arguments_,
            state: 'result',
          });
          applyResult(assistantId, { toolCallId: event.toolCallId, result: undefined, state: 'result' });
          emitToolResult(assistantId, event.toolCallId, {
            outcome: 'error',
            summary: `Input error: ${event.errorText}`,
            defaultName: event.toolName,
          });
          return true;
        }
        case 'tool-output-available': {
          const result = normalizeToolResult(event.output);
          log.info('Parsed event: tool-output-available', {
            toolCallId: event.toolCallId,
            preliminary: event.preliminary === true,
          });
          applyResult(assistantId, {
            toolCallId: event.toolCallId,
            result,
            state: event.preliminary ? 'partial-call' : 'result',
          });
          if (!event.preliminary) {
            const summary = buildResultSummary(result);
            emitToolResult(assistantId, event.toolCallId, {
              outcome: 'success',
              summary,
              defaultName: event.toolCallId,
            });
          }
          return true;
        }
        case 'tool-output-error': {
          log.error('Parsed event: tool-output-error', {
            toolCallId: event.toolCallId,
            error: event.errorText,
          });
          applyResult(assistantId, { toolCallId: event.toolCallId, result: null, state: 'result' });
          emitToolResult(assistantId, event.toolCallId, {
            outcome: 'error',
            summary: `Execution error: ${event.errorText}`,
            defaultName: 'tool',
          });
          return true;
        }
        case 'tool-result': {
          const result = normalizeToolResult(event.result);
          log.info('Parsed event: tool-result', { toolCallId: event.toolCallId });
          applyResult(assistantId, { toolCallId: event.toolCallId, result, state: 'result' });
          emitToolResult(assistantId, event.toolCallId, {
            outcome: 'success',
            summary: buildResultSummary(result),
            defaultName: event.toolCallId,
          });
          return true;
        }
        case 'tool-input-delta': {
          log.debug('Parsed event: tool-input-delta', {
            toolCallId: event.toolCallId,
            deltaLength: event.inputTextDelta.length,
          });
          return true;
        }
        default: {
          log.warn('Parsed event: unexpected tool payload received', { payload: event });
          return false;
        }
      }
    },
    [
      appendReasoning,
      appendToolCall,
      applyResult,
      buildArgumentsSummary,
      buildResultSummary,
      createReasoningEntry,
      emitToolResult,
      log,
      updateReasoning,
      onUiEvent,
    ]
  );

  return { handleToolEvent };
};
